
import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'uk';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Header
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.getQuote': 'Get a Quote',
    'contact.phone': '+380 63 988 4342',
    'contact.email': 'info@nkcargo.com',
    'contact.global': 'China · USA · Europe → Ukraine',

    // Hero
    'hero.badge': 'End-to-end international logistics',
    'hero.title1': 'Cargo delivery',
    'hero.title2': 'from China, USA & Europe',
    'hero.title3': 'to Ukraine',
    'hero.subtitle': 'Air, sea and road freight for businesses and individuals: consolidated cargo, commercial shipments, customs clearance included. A personal manager keeps you updated at every stage.',
    'hero.getQuote': 'Get a quote in 30 min',
    'hero.telegram': 'Message us on Telegram',
    'hero.dir.china': 'China → Ukraine',
    'hero.dir.usa': 'USA → Ukraine',
    'hero.dir.europe': 'Europe ↔ Ukraine',
    'hero.dir.label': 'Delivery routes',

    // Services
    'services.title': 'Our Services',
    'services.subtitle': 'We handle the whole chain: pickup from your supplier, international freight, customs clearance and delivery in Ukraine.',
    'services.air.title': 'Air Freight',
    'services.air.description': 'Fast air delivery from China and the USA for urgent and high-value shipments.',
    'services.sea.title': 'Sea Freight',
    'services.sea.description': 'The most economical option for large shipments from China: consolidated cargo (LCL) and full containers (FCL).',
    'services.auto.title': 'Road Freight',
    'services.auto.description': 'Truck delivery between Europe and Ukraine: groupage and full loads, door-to-door.',
    'services.customs.title': 'Customs Clearance',
    'services.customs.description': 'Full customs support: documents, duty calculation and clearance without your involvement.',
    'services.additional.title': 'Additional Services',
    'services.additional.subtitle': 'Everything you need around the shipment itself',
    'services.express.title': 'Always in Touch',
    'services.express.description': 'Telegram, Viber, WhatsApp — we answer quickly during business hours',
    'services.insurance.title': 'Cargo Insurance',
    'services.insurance.description': 'Optional coverage for your shipments',
    'services.network.title': 'Verified Partners',
    'services.network.description': 'Trusted agents and carriers in China, the USA and Europe',
    'services.swift.title': 'SWIFT Payments',
    'services.swift.description': 'International payments to your suppliers',
    'services.yuan.title': 'Yuan Payments',
    'services.yuan.description': 'Direct transactions in Chinese currency',

    // Service Features
    'services.air.features.faster': 'Fastest transit option',
    'services.air.features.network': 'China & USA departures',
    'services.air.features.tracking': 'Status updates from your manager',
    'services.air.features.control': 'Door-to-door available',
    'services.sea.features.fcl': 'FCL & LCL',
    'services.sea.features.port': 'Port-to-Port',
    'services.sea.features.door': 'Door-to-Door',
    'services.sea.features.insurance': 'Cargo insurance available',
    'services.auto.features.groupage': 'Groupage & full loads',
    'services.auto.features.door': 'Door-to-Door',
    'services.auto.features.customs': 'Customs clearance included',
    'services.customs.features.document': 'Document Prep',
    'services.customs.features.duty': 'Duty Calculation',
    'services.customs.features.compliance': 'Compliance Check',
    'services.customs.features.processing': 'Fast Processing',

    // About
    'about.title': 'About NK Cargo',
    'about.subtitle': 'A logistics company based in Dnipro, Ukraine. We organize freight from China, the USA and Europe to Ukraine.',
    'about.story.title': 'What We Do',
    'about.story.text1': 'NK Cargo organizes cargo delivery to Ukraine by air, sea and road. We work with businesses and individuals alike: from small consolidated shipments to full commercial consignments.',
    'about.story.text2': 'We take over the whole chain — pickup from the supplier, international freight, customs clearance and handover in Ukraine. You get one point of contact: a personal manager who knows where your cargo is at any moment.',
    'about.mission.title': 'Our Approach',
    'about.mission.text': 'Honest terms up front: we quote real timelines and real costs before the shipment, not after.',
    'about.vision.title': 'Our Format',
    'about.vision.text': 'A personal manager accompanies your cargo from pickup to handover and keeps you updated at every stage.',
    'about.whyChoose.title': 'Why NK Cargo?',
    'about.support247': 'Quick replies in messengers',
    'about.tracking': 'Manager updates you at every stage',
    'about.pricing': 'Transparent pricing, no hidden fees',
    'about.network': 'Verified partners in China, USA and Europe',
    'about.team': 'Experienced logistics team',
    'about.facts.routes.title': '3 Routes',
    'about.facts.routes.text': 'China, USA and Europe to Ukraine',
    'about.facts.modes.title': 'Air · Sea · Road',
    'about.facts.modes.text': 'We pick the mode that fits your cargo and budget',
    'about.facts.clients.title': 'B2B & Individuals',
    'about.facts.clients.text': 'From single parcels to commercial consignments',
    'about.facts.customs.title': 'Customs Included',
    'about.facts.customs.text': 'Documents, duties and clearance handled for you',
    'about.values.title': 'How We Work',
    'about.values.subtitle': 'The principles behind every shipment we handle.',
    'about.reliability.title': 'Reliability',
    'about.reliability.text': 'We plan each shipment so your cargo arrives safely and without surprises.',
    'about.globalReach.title': 'Transparency',
    'about.globalReach.text': 'Real timelines and full cost before departure — you always know what you pay for.',
    'about.innovation.title': 'Support',
    'about.innovation.text': 'We stay in touch after departure — until the cargo is in your hands.',

    // Reviews
    'nav.reviews': 'Reviews',
    'reviews.title': 'What Our Clients Say',
    'reviews.subtitle': 'Real feedback from businesses that ship with NK Cargo',
    'reviews.cta.title': 'Ready to ship with us?',
    'reviews.cta.subtitle': 'Get a quote within 30 minutes during business hours — or message us directly.',
    'reviews.cta.button': 'Get a Quote',
    'reviews.testimonial1.name': 'Olena Kravchenko',
    'reviews.testimonial1.position': 'Online Store Owner',
    'reviews.testimonial1.company': 'Luma Home',
    'reviews.testimonial1.city': 'Kyiv, Ukraine',
    'reviews.testimonial1.text': 'We ordered a large batch of home decor ahead of our autumn launch. The supplier delayed the shipment even though part of the stock had already been sold through pre-orders. I was getting ready to refund customers and postpone the ad campaign. Kristina stayed in touch, explained the situation without complicated logistics jargon and gave us a heads-up before every next step. The batch arrived in time for the launch, so no orders had to be cancelled.',
    'reviews.testimonial2.name': 'Andrii Melnyk',
    'reviews.testimonial2.position': 'Operations Manager',
    'reviews.testimonial2.company': 'Promline Systems',
    'reviews.testimonial2.city': 'Dnipro, Ukraine',
    'reviews.testimonial2.text': 'We were waiting for components for a production line with literally a few working days of stock left. Usually the worst part of situations like this is complete silence from the carrier and no way to plan anything. Here we regularly received clear, meaningful updates. Thanks to that, we adjusted the production schedule in time and avoided a full line stoppage.',
    'reviews.testimonial3.name': 'Kateryna Savchuk',
    'reviews.testimonial3.position': 'Import Director',
    'reviews.testimonial3.company': 'Westbridge Distribution',
    'reviews.testimonial3.city': 'Lviv, Ukraine',
    'reviews.testimonial3.text': "We discovered a discrepancy in the supplier's documents after the cargo had already been shipped. A similar situation in the past ended in a long customs hold, extra costs and missed deliveries to our partners. This time we quickly got an explanation of what data was missing and what had to be corrected. The issue was closed without endless email threads or parties shifting responsibility onto each other.",
    'reviews.testimonial4.name': 'Thomas Becker',
    'reviews.testimonial4.position': 'Logistics Coordinator',
    'reviews.testimonial4.company': 'Nordwerk Solutions',
    'reviews.testimonial4.city': 'Hamburg, Germany',
    'reviews.testimonial4.text': 'We were shipping sensitive equipment to a client site in Warsaw. Some of the components were difficult to replace, so even minor damage could have delayed the entire installation. The cargo arrived in proper condition and was handled carefully throughout the process. For us, that mattered more than receiving polished promises or constant sales messages.',
    'reviews.testimonial5.name': 'Maria Santos',
    'reviews.testimonial5.position': 'Procurement Director',
    'reviews.testimonial5.company': 'Ardentis Group',
    'reviews.testimonial5.city': 'Lisbon, Portugal',
    'reviews.testimonial5.text': 'Before this cooperation, every shipment involved a new carrier, new contacts and the same explanations about our paperwork and delivery requirements. It was taking too much time from our procurement team. After several shipments, the process now feels much more predictable. Kristina already knows how we work, responds quickly when something changes and does not disappear when an issue needs to be resolved.',

    // Contact
    'contact.title': 'Get a Quote',
    'contact.subtitle': 'Leave a request — a manager will get back to you within 30 minutes during business hours.',
    'contact.form.title': 'Request a Quote',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.email.optional': 'optional',
    'contact.form.phone': 'Phone Number',
    'contact.form.send': 'Get a Quote',
    'contact.form.sending': 'Sending...',
    'contact.form.deliveryType': 'Delivery Type',
    'contact.form.deliveryType.sea': 'Sea',
    'contact.form.deliveryType.air': 'Air',
    'contact.form.cityOfDeparture': 'From (city, country)',
    'contact.form.deliveryCity': 'To (city in Ukraine)',
    'contact.form.placeholder.name': 'Taras',
    'contact.form.placeholder.email': 'taras@company.com',
    'contact.form.placeholder.phone': '+380 63 000 00 00',
    'contact.form.placeholder.cityOfDeparture': 'Guangzhou, China',
    'contact.form.placeholder.deliveryCity': 'Kyiv',
    'contact.form.note': 'We will call back within 30 minutes during business hours. No spam.',
    'contact.messengers.title': 'Faster in a messenger:',
    'contact.success.title': 'Request received!',
    'contact.success.text': 'A manager will contact you within 30 minutes during business hours. Want it faster? Message us directly:',
    'contact.success.another': 'Send another request',
    'contact.locations.title': 'Our Office',
    'contact.address.office': 'Barykadna St, 22A, Dnipro, Ukraine, 49000',
    'contact.phone.title': 'Phone & Messengers',
    'contact.email.title': 'Email',
    'contact.hours.title': 'Business Hours',
    'contact.hours.weekdays': 'Monday - Friday: 8:00 AM - 6:00 PM',
    'contact.hours.saturday': 'Saturday: 9:00 AM - 4:00 PM',
    'contact.hours.emergency': 'Messengers: we reply as fast as we can',
    'contact.map.title': 'Find us!',
    'contact.map.subtitle': "Let's talk in person 😉",
    'contact.map.openDirections': 'Get Directions',
    'contact.toast.title': 'Request sent!',
    'contact.toast.description': 'Thank you! A manager will contact you within 30 minutes during business hours.',
    'contact.toast.error.title': 'Failed to send',

    // Footer
    'footer.description': 'NK Cargo — cargo delivery from China, the USA and Europe to Ukraine. Air, sea and road freight with customs clearance included.',
    'footer.services.title': 'Services',
    'footer.services.air': 'Air Freight',
    'footer.services.sea': 'Sea Freight',
    'footer.services.auto': 'Road Freight',
    'footer.services.customs': 'Customs Clearance',
    'footer.company.title': 'Company',
    'footer.company.about': 'About Us',
    'footer.company.team': 'Our Team',
    'footer.support.title': 'Support',
    'footer.support.contact': 'Contact Us',
    'footer.messengers': 'Message us:',
    'footer.worldwide': 'China · USA · Europe → Ukraine',
    'footer.copyright': '© 2026 NK Cargo. All rights reserved.'
  },
  uk: {
    // Header
    'nav.home': 'Головна',
    'nav.services': 'Послуги',
    'nav.about': 'Про нас',
    'nav.contact': 'Контакти',
    'nav.getQuote': 'Отримати прорахунок',
    'contact.phone': '+380 63 988 4342',
    'contact.email': 'info@nkcargo.com',
    'contact.global': 'Китай · США · Європа → Україна',

    // Hero
    'hero.badge': 'Міжнародна логістика під ключ',
    'hero.title1': 'Доставка вантажів',
    'hero.title2': 'з Китаю, США та Європи',
    'hero.title3': 'в Україну',
    'hero.subtitle': 'Авіа, море та авто для бізнесу і приватних клієнтів: збірні вантажі, комерційні партії, митне оформлення під ключ. Персональний менеджер тримає вас у курсі на кожному етапі.',
    'hero.getQuote': 'Прорахунок за 30 хвилин',
    'hero.telegram': 'Написати в Telegram',
    'hero.dir.china': 'Китай → Україна',
    'hero.dir.usa': 'США → Україна',
    'hero.dir.europe': 'Європа ↔ Україна',
    'hero.dir.label': 'Напрямки доставки',

    // Services
    'services.title': 'Наші послуги',
    'services.subtitle': 'Беремо на себе весь ланцюжок: забір вантажу у постачальника, міжнародне перевезення, розмитнення і доставку по Україні.',
    'services.air.title': 'Авіадоставка',
    'services.air.description': 'Швидка авіадоставка з Китаю та США для термінових і цінних вантажів.',
    'services.sea.title': 'Морська доставка',
    'services.sea.description': 'Найекономніший варіант для великих партій з Китаю: збірні вантажі (LCL) та повні контейнери (FCL).',
    'services.auto.title': 'Автодоставка',
    'services.auto.description': 'Автоперевезення між Європою та Україною: збірні та повні завантаження, від дверей до дверей.',
    'services.customs.title': 'Митне оформлення',
    'services.customs.description': 'Повний митний супровід: документи, розрахунок платежів і розмитнення без вашої участі.',
    'services.additional.title': 'Додаткові послуги',
    'services.additional.subtitle': 'Все, що потрібно навколо самого перевезення',
    'services.express.title': 'Завжди на звʼязку',
    'services.express.description': 'Telegram, Viber, WhatsApp — швидко відповідаємо в робочий час',
    'services.insurance.title': 'Страхування вантажу',
    'services.insurance.description': 'Опційне покриття для ваших відправлень',
    'services.network.title': 'Перевірені партнери',
    'services.network.description': 'Надійні агенти й перевізники в Китаї, США та Європі',
    'services.swift.title': 'SWIFT-платежі',
    'services.swift.description': 'Міжнародні оплати вашим постачальникам',
    'services.yuan.title': 'Платежі в юанях',
    'services.yuan.description': 'Прямі розрахунки в китайській валюті',

    // Service Features
    'services.air.features.faster': 'Найшвидший варіант доставки',
    'services.air.features.network': 'Відправки з Китаю та США',
    'services.air.features.tracking': 'Статуси від персонального менеджера',
    'services.air.features.control': 'Можлива доставка до дверей',
    'services.sea.features.fcl': 'FCL і LCL',
    'services.sea.features.port': 'Порт-в-порт',
    'services.sea.features.door': 'Двері-в-двері',
    'services.sea.features.insurance': 'Можливе страхування вантажу',
    'services.auto.features.groupage': 'Збірні та повні завантаження',
    'services.auto.features.door': 'Двері-в-двері',
    'services.auto.features.customs': 'Розмитнення включено',
    'services.customs.features.document': 'Підготовка документів',
    'services.customs.features.duty': 'Розрахунок платежів',
    'services.customs.features.compliance': 'Перевірка відповідності',
    'services.customs.features.processing': 'Швидке оформлення',

    // About
    'about.title': 'Про NK Cargo',
    'about.subtitle': 'Логістична компанія з офісом у Дніпрі. Організовуємо доставку вантажів з Китаю, США та Європи в Україну.',
    'about.story.title': 'Чим ми займаємось',
    'about.story.text1': 'NK Cargo організовує доставку вантажів в Україну авіа, морем та авто. Працюємо і з бізнесом, і з приватними клієнтами: від невеликих збірних відправлень до комерційних партій.',
    'about.story.text2': 'Беремо на себе весь ланцюжок — забір вантажу у постачальника, міжнародне перевезення, митне оформлення і видачу в Україні. У вас одна точка контакту: персональний менеджер, який у будь-який момент знає, де ваш вантаж.',
    'about.mission.title': 'Наш підхід',
    'about.mission.text': 'Чесні умови одразу: називаємо реальні терміни і повну вартість до відправки, а не після.',
    'about.vision.title': 'Наш формат',
    'about.vision.text': 'Персональний менеджер супроводжує вантаж від забору до видачі й інформує вас на кожному етапі.',
    'about.whyChoose.title': 'Чому NK Cargo?',
    'about.support247': 'Швидкі відповіді в месенджерах',
    'about.tracking': 'Менеджер інформує про статус на кожному етапі',
    'about.pricing': 'Прозорі тарифи без прихованих платежів',
    'about.network': 'Перевірені партнери в Китаї, США та Європі',
    'about.team': 'Досвідчена команда логістів',
    'about.facts.routes.title': '3 напрямки',
    'about.facts.routes.text': 'Китай, США та Європа → Україна',
    'about.facts.modes.title': 'Авіа · Море · Авто',
    'about.facts.modes.text': 'Підбираємо спосіб під ваш вантаж і бюджет',
    'about.facts.clients.title': 'B2B і приватні',
    'about.facts.clients.text': 'Від однієї посилки до комерційних партій',
    'about.facts.customs.title': 'Митниця під ключ',
    'about.facts.customs.text': 'Документи, платежі й розмитнення — на нас',
    'about.values.title': 'Як ми працюємо',
    'about.values.subtitle': 'Принципи, за якими проходить кожне наше відправлення.',
    'about.reliability.title': 'Надійність',
    'about.reliability.text': 'Плануємо кожне перевезення так, щоб вантаж доїхав безпечно і без сюрпризів.',
    'about.globalReach.title': 'Прозорість',
    'about.globalReach.text': 'Реальні терміни і повна вартість до відправки — ви завжди знаєте, за що платите.',
    'about.innovation.title': 'Супровід',
    'about.innovation.text': 'Залишаємось на звʼязку після відправки — до моменту, коли вантаж у вас.',

    // Reviews
    'nav.reviews': 'Відгуки',
    'reviews.title': 'Що кажуть наші клієнти',
    'reviews.subtitle': 'Реальні відгуки клієнтів, які возять вантажі з NK Cargo',
    'reviews.cta.title': 'Готові відправити вантаж?',
    'reviews.cta.subtitle': 'Отримайте прорахунок протягом 30 хвилин у робочий час — або напишіть нам у месенджер.',
    'reviews.cta.button': 'Отримати прорахунок',
    'reviews.testimonial1.name': 'Олена Кравченко',
    'reviews.testimonial1.position': 'Власниця інтернет-магазину',
    'reviews.testimonial1.company': 'Luma Home',
    'reviews.testimonial1.city': 'Київ, Україна',
    'reviews.testimonial1.text': 'Замовили велику партію декору перед осіннім запуском. Постачальник затримав відвантаження, хоча частина товару вже була продана за передзамовленням. Я готувалася повертати клієнтам гроші та переносити рекламну кампанію. Кристина була на звʼязку, пояснювала ситуацію без складних логістичних термінів і попереджала про кожен наступний етап. Партія приїхала вчасно для запуску, тому обійшлося без скасованих замовлень.',
    'reviews.testimonial2.name': 'Андрій Мельник',
    'reviews.testimonial2.position': 'Операційний менеджер',
    'reviews.testimonial2.company': 'Promline Systems',
    'reviews.testimonial2.city': 'Дніпро, Україна',
    'reviews.testimonial2.text': 'Чекали комплектуючі для виробничої лінії, а запасів на складі залишалося буквально на кілька робочих днів. Зазвичай у таких ситуаціях найгірше — повна тиша від перевізника і неможливість щось спланувати. Тут ми регулярно отримували нормальні, зрозумілі оновлення. Завдяки цьому вчасно змінили графік виробництва й не допустили простою всієї лінії.',
    'reviews.testimonial3.name': 'Катерина Савчук',
    'reviews.testimonial3.position': 'Директорка з імпорту',
    'reviews.testimonial3.company': 'Westbridge Distribution',
    'reviews.testimonial3.city': 'Львів, Україна',
    'reviews.testimonial3.text': 'У документах від постачальника виявили невідповідність уже після відправлення вантажу. Раніше схожа ситуація закінчилася для нас довгим простоєм на митниці, додатковими витратами та зірваними поставками партнерам. Цього разу нам швидко пояснили, яких даних бракує і що потрібно виправити. Питання закрили без нескінченного листування та перекидання відповідальності між сторонами.',
    'reviews.testimonial4.name': 'Томас Бекер',
    'reviews.testimonial4.position': 'Координатор з логістики',
    'reviews.testimonial4.company': 'Nordwerk Solutions',
    'reviews.testimonial4.city': 'Гамбург, Німеччина',
    'reviews.testimonial4.text': 'Ми відправляли чутливе обладнання на обʼєкт клієнта у Варшаві. Частину компонентів складно замінити, тож навіть незначне пошкодження могло затримати весь монтаж. Вантаж прибув у належному стані, і з ним обережно поводилися на кожному етапі. Для нас це важило більше, ніж гарні обіцянки чи постійні повідомлення від відділу продажів.',
    'reviews.testimonial5.name': 'Марія Сантос',
    'reviews.testimonial5.position': 'Директорка із закупівель',
    'reviews.testimonial5.company': 'Ardentis Group',
    'reviews.testimonial5.city': 'Лісабон, Португалія',
    'reviews.testimonial5.text': 'До цієї співпраці кожне відправлення означало нового перевізника, нові контакти й ті самі пояснення щодо наших документів і вимог до доставки. Це забирало забагато часу в нашої команди закупівель. Після кількох відправлень процес став значно передбачуванішим. Кристина вже знає, як ми працюємо, швидко реагує на зміни й не зникає, коли питання треба вирішити.',

    // Contact
    'contact.title': 'Отримати прорахунок',
    'contact.subtitle': 'Залиште заявку — менеджер звʼяжеться з вами протягом 30 хвилин у робочий час.',
    'contact.form.title': 'Заявка на прорахунок',
    'contact.form.name': 'Імʼя',
    'contact.form.email': 'Email',
    'contact.form.email.optional': 'необовʼязково',
    'contact.form.phone': 'Номер телефону',
    'contact.form.send': 'Отримати прорахунок',
    'contact.form.sending': 'Надсилання...',
    'contact.form.deliveryType': 'Тип доставки',
    'contact.form.deliveryType.sea': 'Море',
    'contact.form.deliveryType.air': 'Авіа',
    'contact.form.cityOfDeparture': 'Звідки (місто, країна)',
    'contact.form.deliveryCity': 'Куди (місто в Україні)',
    'contact.form.placeholder.name': 'Тарас',
    'contact.form.placeholder.email': 'taras@company.com',
    'contact.form.placeholder.phone': '+380 63 000 00 00',
    'contact.form.placeholder.cityOfDeparture': 'Гуанчжоу, Китай',
    'contact.form.placeholder.deliveryCity': 'Київ',
    'contact.form.note': 'Передзвонимо протягом 30 хвилин у робочий час. Без спаму.',
    'contact.messengers.title': 'Швидше в месенджері:',
    'contact.success.title': 'Заявку отримано!',
    'contact.success.text': 'Менеджер звʼяжеться з вами протягом 30 хвилин у робочий час. Хочете швидше? Напишіть нам напряму:',
    'contact.success.another': 'Надіслати ще одну заявку',
    'contact.locations.title': 'Наш офіс',
    'contact.address.office': 'вул. Барикадна, 22А, Дніпро, Україна, 49000',
    'contact.phone.title': 'Телефон і месенджери',
    'contact.email.title': 'Email',
    'contact.hours.title': 'Робочий час',
    'contact.hours.weekdays': 'Понеділок - Пʼятниця: 8:00 - 18:00',
    'contact.hours.saturday': 'Субота: 9:00 - 16:00',
    'contact.hours.emergency': 'У месенджерах відповідаємо максимально швидко',
    'contact.map.title': 'Знайдіть нас!',
    'contact.map.subtitle': 'Поговорімо особисто 😉',
    'contact.map.openDirections': 'Прокласти маршрут',
    'contact.toast.title': 'Заявку надіслано!',
    'contact.toast.description': 'Дякуємо! Менеджер звʼяжеться з вами протягом 30 хвилин у робочий час.',
    'contact.toast.error.title': 'Не вдалося надіслати',

    // Footer
    'footer.description': 'NK Cargo — доставка вантажів з Китаю, США та Європи в Україну. Авіа, морські та автоперевезення з митним оформленням під ключ.',
    'footer.services.title': 'Послуги',
    'footer.services.air': 'Авіадоставка',
    'footer.services.sea': 'Морська доставка',
    'footer.services.auto': 'Автодоставка',
    'footer.services.customs': 'Митне оформлення',
    'footer.company.title': 'Компанія',
    'footer.company.about': 'Про нас',
    'footer.company.team': 'Наша команда',
    'footer.support.title': 'Підтримка',
    'footer.support.contact': 'Звʼязатися з нами',
    'footer.messengers': 'Напишіть нам:',
    'footer.worldwide': 'Китай · США · Європа → Україна',
    'footer.copyright': '© 2026 NK Cargo. Всі права захищені.'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('uk');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
